import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { leadSx, noteSx, sentLineSx, stressSx } from './codeStepStyles';

const MARK = '\uE000';

const splitAtMark = (text: string): readonly [string, string] | null => {
  const at = text.indexOf(MARK);
  return at < 0 ? null : [text.slice(0, at), text.slice(at + MARK.length)];
};

interface SentLineProps {
  readonly id: string;
  readonly channel?: string;
  readonly target: string;
}

const SentLine: React.FC<SentLineProps> = ({ id, channel, target }) => {
  const { t } = useTranslation();
  const template =
    channel === undefined
      ? t('coreComponents.codeStep.sentTo', 'Sent to {{target}}', { target: MARK })
      : t('coreComponents.codeStep.sentOn', 'Sent on {{channel}} to {{target}}', { channel: MARK, target });
  const stressed = channel ?? target;
  const parts = splitAtMark(template);
  return (
    <Typography id={id} variant="body" component="p" sx={sentLineSx}>
      {parts === null ? (
        template
      ) : (
        <>
          {parts[0]}
          <Box component="span" sx={stressSx}>
            {stressed}
          </Box>
          {parts[1]}
        </>
      )}
    </Typography>
  );
};

export interface CodeStepNote {
  readonly id: string;
  readonly text: string;
}

export interface CodeStepLeadProps {
  readonly sentLineId: string;
  readonly channel?: string;
  readonly target: string;
  readonly notes: readonly CodeStepNote[];
}

export const CodeStepLead: React.FC<CodeStepLeadProps> = ({ sentLineId, channel, target, notes }) => (
  <Box sx={leadSx}>
    <SentLine id={sentLineId} channel={channel} target={target} />
    {notes.map((note) => (
      <Typography key={note.id} id={note.id} variant="label" component="p" sx={noteSx}>
        {note.text}
      </Typography>
    ))}
  </Box>
);
